import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Home, Building2, Eye, Sparkles,
  MessageSquareText, CheckSquare, ClipboardList, CalendarDays, LogOut, Menu, X, Kanban,
  BookOpen, Target, Ruler,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import QuickAdd from "@/components/QuickAdd";
import SyncStatusBadge from "@/components/SyncStatusBadge";

const navItems = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, testId: "nav-dashboard-link" },
  { to: "/leads", key: "leads", icon: Target, testId: "nav-leads-link" },
  { to: "/pipeline", key: "pipeline", icon: Kanban, testId: "nav-pipeline-link" },
  { to: "/calendar", key: "calendar", icon: CalendarDays, testId: "nav-calendar-link" },
  { to: "/buyers", key: "buyers", icon: Users, testId: "nav-buyers-link" },
  { to: "/sellers", key: "sellers", icon: Home, testId: "nav-sellers-link" },
  { to: "/properties", key: "properties", icon: Building2, testId: "nav-properties-link" },
  { to: "/rooms", key: "rooms", icon: Ruler, testId: "nav-rooms-link" },
  { to: "/viewings", key: "viewings", icon: Eye, testId: "nav-viewings-link" },
  { to: "/forms", key: "forms", icon: ClipboardList, testId: "nav-forms-link" },
  { to: "/ai", key: "aiGenerator", icon: Sparkles, testId: "nav-ai-link" },
  { to: "/templates", key: "templates", icon: MessageSquareText, testId: "nav-templates-link" },
  { to: "/tasks", key: "tasks", icon: CheckSquare, testId: "nav-tasks-link" },
];

const LangToggle = () => {
  const { lang, switchLang } = useLang();
  return (
    <div className="inline-flex rounded-full border border-stone-200 bg-stone-100 p-0.5">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => switchLang(l)}
          className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors ${
            lang === l ? "bg-slate-900 text-white" : "text-stone-500 hover:text-stone-800"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const NavLinks = ({ onClick }) => {
  const { t } = useLang();
  return (
    <nav className="flex-1 overflow-y-auto space-y-0.5 px-3 pb-2">
      {navItems.map(({ to, key, icon: Icon, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/dashboard"}
          onClick={onClick}
          data-testid={testId}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-slate-900 text-white"
                : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
            }`
          }
        >
          <Icon size={18} strokeWidth={1.5} />
          {t(key)}
        </NavLink>
      ))}
    </nav>
  );
};

const Brand = () => (
  <div className="px-6 pt-5 pb-3">
    <span className="font-heading text-2xl font-medium text-stone-900">Julia</span>
    <span className="font-heading text-2xl font-medium italic text-[#9F8E7E]"> AI</span>
    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-0.5">Real Estate Assistant</p>
  </div>
);

export const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-stone-200">
        <Brand />
        <div className="px-6 pb-3 flex flex-col items-start gap-2">
          <LangToggle />
          <SyncStatusBadge />
        </div>
        <NavLinks />
        <div className="px-6 py-4 border-t border-stone-200">
          <NavLink
            to="/profile"
            data-testid="profile-link"
            className="flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-stone-200" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 text-xs font-semibold">
                {(user?.name || user?.email || "?")[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-800 truncate">{user?.name}</p>
              <p className="text-[11px] text-stone-400 truncate">{user?.email}</p>
            </div>
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs font-medium text-stone-500 hover:text-stone-900 transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-stone-200 flex items-center justify-between px-4 py-3">
        <div>
          <span className="font-heading text-xl font-medium text-stone-900">Julia</span>
          <span className="font-heading text-xl font-medium italic text-[#9F8E7E]"> AI</span>
        </div>
        <div className="flex items-center gap-2">
          <SyncStatusBadge />
          <LangToggle />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 rounded-lg hover:bg-stone-100 text-stone-700"
          >
            {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-white pt-16 flex flex-col">
          <div className="pt-4 flex-1 overflow-y-auto">
            <NavLinks onClick={() => setMobileOpen(false)} />
          </div>
          <div className="px-6 py-5 border-t border-stone-200">
            <p className="text-sm font-medium text-stone-800">{user?.name}</p>
            <button
              data-testid="mobile-logout-button"
              onClick={handleLogout}
              className="mt-2 flex items-center gap-2 text-xs font-medium text-stone-500"
            >
              <LogOut size={14} strokeWidth={1.5} /> {t("logout")}
            </button>
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12 animate-page-in">
          <QuickAdd />
          {children}
        </div>
      </main>
    </div>
  );
};
